import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, CheckCircle, XCircle, Clock, Eye, Download } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

export default function AdminReportsPage() {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const toast = useToast();

  useEffect(() => {
    loadStudents();
  }, []);

  async function loadStudents() {
    try {
      setLoading(true);
      const { data } = await api.get('/analytics/students-progress');
      setStudents(data);
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
      toast.error('Erro ao carregar alunos');
    } finally {
      setLoading(false);
    }
  }

  async function loadStudentDetails(studentId) {
    try {
      const { data } = await api.get(`/analytics/student/${studentId}/detailed-progress`);
      setStudentDetails(data);
      setSelectedStudent(studentId);
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      toast.error('Erro ao carregar detalhes do aluno');
    }
  }

  const filteredStudents = students.filter(s => 
    s.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <svg className="w-8 h-8 animate-spin text-primary-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Relatório de Progresso
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Visualização detalhada do progresso de cada aluno
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de Alunos */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-background-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Buscar aluno..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-background-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:border-primary-500 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
              {filteredStudents.map((student) => (
                <button
                  key={student.id}
                  onClick={() => loadStudentDetails(student.id)}
                  className={`w-full p-4 text-left border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-background-700 transition ${
                    selectedStudent === student.id ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-l-primary-500' : ''
                  }`}
                >
                  <div className="font-medium text-gray-900 dark:text-white mb-1">
                    {student.full_name}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    {student.email}
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                      <Eye size={14} />
                      {student.videosWatched}
                    </span>
                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <CheckCircle size={14} />
                      {student.videosCompleted}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Detalhes do Aluno */}
        <div className="lg:col-span-2">
          {!studentDetails ? (
            <div className="bg-white dark:bg-background-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
              <Eye className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-gray-500 dark:text-gray-400">
                Selecione um aluno para ver os detalhes
              </p>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Info do Aluno */}
              <div className="bg-white dark:bg-background-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                      {studentDetails.student.full_name}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                      {studentDetails.student.email}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    studentDetails.student.is_active
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  }`}>
                    {studentDetails.student.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-gray-50 dark:bg-background-900 rounded-lg">
                    <div className="text-2xl font-bold text-primary-500">
                      {studentDetails.modules.length}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Módulos</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 dark:bg-background-900 rounded-lg">
                    <div className="text-2xl font-bold text-blue-500">
                      {studentDetails.modules.reduce((acc, m) => acc + m.totalVideos, 0)}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Vídeos Total</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 dark:bg-background-900 rounded-lg">
                    <div className="text-2xl font-bold text-green-500">
                      {studentDetails.modules.reduce((acc, m) => acc + m.completedVideos, 0)}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Concluídos</div>
                  </div>
                </div>
              </div>

              {/* Módulos e Vídeos */}
              {studentDetails.modules.map((module) => (
                <div
                  key={module.module.id}
                  className="bg-white dark:bg-background-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  <div className="p-4 bg-gray-50 dark:bg-background-900 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {module.module.title_pt}
                      </h3>
                      <span className="text-sm font-medium text-primary-500">
                        {module.completionPercentage}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-primary-500 to-accent-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${module.completionPercentage}%` }}
                      />
                    </div>
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                      {module.completedVideos} de {module.totalVideos} vídeos concluídos
                    </div>
                  </div>

                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {module.videos.map((video, index) => {
                      const progress = video.progress;
                      const percentage = video.duration_seconds > 0
                        ? Math.round((progress.watched_seconds / video.duration_seconds) * 100)
                        : 0;

                      return (
                        <div key={video.id} className="p-4 hover:bg-gray-50 dark:hover:bg-background-700 transition">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-1">
                              {progress.is_completed ? (
                                <CheckCircle className="text-green-500" size={20} />
                              ) : progress.watched_seconds > 0 ? (
                                <Clock className="text-yellow-500" size={20} />
                              ) : (
                                <XCircle className="text-gray-400" size={20} />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                                  {index + 1}. {video.title_pt}
                                </h4>
                                <span className={`text-xs font-semibold px-2 py-1 rounded ${
                                  progress.is_completed
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : progress.watched_seconds > 0
                                    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                }`}>
                                  {progress.is_completed ? 'Completo' : progress.watched_seconds > 0 ? `${percentage}%` : 'Não iniciado'}
                                </span>
                              </div>
                              {progress.watched_seconds > 0 && (
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                  Assistiu: {Math.floor(progress.watched_seconds / 60)}min de {Math.floor(video.duration_seconds / 60)}min
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
